import { splitFrontmatterDocument } from './index'

// Fold state rides note frontmatter under a `folded:` key, sibling to
// `reminders:`. Slugs are serialized JSON-quoted (like reminder scalars) so
// headings named "null" or "123" can never be misread by a YAML parser.
//
//   ---
//   folded:
//     - "my-section"
//     - "another-section-2"
//   ---
//
// Mirrored 1:1 by FoldFrontmatter.swift in CaliuCore (parsed-set parity, not
// byte parity - the Swift side follows its local frontmatter conventions).

const FRONTMATTER_BOUNDARY = '---'
const FOLDED_KEY = /^folded:\s*$/
const TOP_LEVEL_KEY = /^[A-Za-z0-9_-]+:\s*/

function normalizeScalar(raw: string): string | null {
  const trimmed = raw.trim()
  if (!trimmed) return null
  if (
    (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
    (trimmed.startsWith("'") && trimmed.endsWith("'"))
  ) {
    return trimmed.slice(1, -1) || null
  }
  return trimmed
}

/** Folded heading slugs stored in the note's frontmatter, document order. */
export function parseFrontmatterFolds(content: string): string[] {
  const { frontmatter } = splitFrontmatterDocument(content)
  if (!frontmatter) return []

  const slugs: string[] = []
  let inFolded = false
  for (const line of frontmatter.split('\n')) {
    if (!inFolded) {
      if (FOLDED_KEY.test(line)) inFolded = true
      continue
    }
    if (TOP_LEVEL_KEY.test(line)) break
    const item = line.match(/^\s{2}-\s*(.*)$/)
    if (item) {
      const slug = normalizeScalar(item[1])
      if (slug && !slugs.includes(slug)) slugs.push(slug)
      continue
    }
    if (!line.trim()) continue
    break
  }
  return slugs
}

/**
 * Rewrite the `folded:` frontmatter section, preserving `reminders:` and any
 * unknown keys (same contract as the reminder rewriter). An empty slug list
 * removes the key; an empty frontmatter block is dropped entirely.
 */
export function updateFrontmatterFolds(content: string, slugs: string[]): string {
  const { frontmatter, body } = splitFrontmatterDocument(content)
  const unique = [...new Set(slugs)]
  const serialized = unique.length > 0
    ? ['folded:', ...unique.map((slug) => `  - ${JSON.stringify(slug)}`)].join('\n')
    : null

  const lines = frontmatter ? frontmatter.split('\n') : []
  const out: string[] = []
  let i = 0
  let replaced = false
  while (i < lines.length) {
    const line = lines[i]
    if (!FOLDED_KEY.test(line)) {
      out.push(line)
      i += 1
      continue
    }
    replaced = true
    if (serialized) out.push(...serialized.split('\n'))
    i += 1
    while (i < lines.length && !TOP_LEVEL_KEY.test(lines[i])) i += 1
  }
  if (!replaced && serialized) {
    if (out.length > 0 && out[out.length - 1] !== '') out.push('')
    out.push(...serialized.split('\n'))
  }

  const next = out.join('\n').replace(/\n{3,}/g, '\n\n').replace(/^\n+|\n+$/g, '')
  if (!next) return body
  return `${FRONTMATTER_BOUNDARY}\n${next}\n${FRONTMATTER_BOUNDARY}\n${body}`
}
